<template>
  <div class="page-container">
    <div class="settings-form">
      <h2 class="page-title">文章草稿任务</h2>

      <!-- 搜索区域 -->
      <div class="search-bar">
        <el-input v-model="searchForm.task_name" placeholder="请输入任务名称" clearable size="large"
          style="width: 300px; margin-right: 15px" @keyup.enter="handleSearch">
          <template #prefix>
            <el-icon>
              <Search />
            </el-icon>
          </template>
        </el-input>

        <el-button type="primary" size="large" @click="handleSearch">
          <el-icon>
            <Search />
          </el-icon>
          搜索
        </el-button>

        <el-button type="success" size="large" @click="openAddDialog" style="float: right">
          <el-icon>
            <Plus />
          </el-icon>
          新增任务
        </el-button>
      </div>

      <!-- 任务列表 -->
      <el-table :data="taskList" stripe style="width: 100%; margin-top: 20px" v-loading="loading">
        <el-table-column prop="task_name" label="任务名称" width="180" />
        <el-table-column label="头条" width="150">
          <template #default="{ row }">
            <span>{{ getGzhName(row.gzh_id) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="文章分类" width="150">
          <template #default="{ row }">
            <span>{{ getCategoryName(row.article_category_id) }}</span>
          </template>
        </el-table-column>
        <!-- 新增的三列 -->
        <el-table-column label="文章总数" width="140" align="center">
          <template #default="{ row }">
            <el-tag type="info" size="small">{{ row.category_total_articles }}</el-tag>
          </template>
        </el-table-column>

        <el-table-column label="已使用文章" width="120" align="center">
          <template #default="{ row }">
            <el-tag type="warning" size="small">{{ row.category_used_articles }}</el-tag>
          </template>
        </el-table-column>

        <el-table-column label="今日已发送" width="120" align="center">
          <template #default="{ row }">
            <el-tag type="success" size="small">{{ row.today_tasks }}</el-tag>
          </template>
        </el-table-column>

        <el-table-column label="每日发布数" width="120">
          <template #default="{ row }">
            <el-tag type="info">{{ row.daily_maximum_release }}</el-tag>
          </template>
        </el-table-column>

        <el-table-column label="已发布总数" width="120">
          <template #default="{ row }">
            <el-tag type="warning">{{ row.total_tasks }}</el-tag>
          </template>
        </el-table-column>

        <el-table-column label="总发布数" width="120">
          <template #default="{ row }">
            <el-tag type="success">{{ row.total_publications }}</el-tag>
          </template>
        </el-table-column>

        <!-- 新增的列 - 放在总发布数后面 -->
        <el-table-column label="错误信息" width="180">
          <template #default="{ row }">
            <el-tooltip v-if="row.error_message" :content="row.error_message" placement="top">
              <el-tag type="danger" size="small">错误</el-tag>
            </el-tooltip>
            <el-tag v-else type="info" size="small">无错误</el-tag>
          </template>
        </el-table-column>

        <el-table-column label="最后发布时间" width="180">
          <template #default="{ row }">
            <span>{{ formatDateTime(row.last_execution_time) || '未发布' }}</span>
          </template>
        </el-table-column>

        <el-table-column prop="create_time" label="创建时间" width="180" />
        <el-table-column prop="update_time" label="更新时间" width="180" />

        <!-- 新增的状态列 - 放在操作列前面 -->
        <el-table-column label="目前状态" width="120" fixed="right">
          <template #default="{ row }">
            <el-tag :type="getStatusTagType(row.status)" size="small">
              {{ getStatusText(row.status) }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column label="启动状态" width="120" fixed="right">
          <template #default="{ row }">
            <el-button :type="row.qd_status === 1 ? 'warning' : 'success'" size="small" @click="toggleTaskStatus(row)"
              :loading="row.statusLoading">
              {{ row.qd_status === 1 ? '暂停任务' : '启动任务' }}
            </el-button>
          </template>
        </el-table-column>

        <el-table-column label="操作" width="180" fixed="right">
          <template #default="scope">
            <el-button type="primary" size="small" @click="openEditDialog(scope.row)">
              <el-icon>
                <Edit />
              </el-icon>
              编辑
            </el-button>
            <el-button type="danger" size="small" @click="handleDelete(scope.row.id)">
              <el-icon>
                <Delete />
              </el-icon>
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页组件 -->
      <div class="pagination-container">
        <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
          :total="pagination.total" :page-sizes="[10, 20, 30, 50]" layout="total, sizes, prev, pager, next, jumper"
          @size-change="fetchTaskList" @current-change="fetchTaskList" />
      </div>
    </div>

    <!-- 新增/编辑对话框 -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="800px" :close-on-click-modal="false">
      <el-form ref="taskForm" :model="taskForm" label-width="140px" :rules="formRules">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="任务名称" prop="task_name">
              <el-input v-model="taskForm.task_name" placeholder="请输入任务名称" size="large" />
            </el-form-item>

            <el-form-item label="头条" prop="gzh_id">
              <el-select v-model="taskForm.gzh_id" placeholder="请选择头条" size="large" style="width: 100%" filterable
                multiple clearable>
                <el-option v-for="gzh in gzhList" :key="gzh.id" :label="gzh.ghz_name" :value="gzh.id" />
              </el-select>
            </el-form-item>

            <el-form-item label="作者名称">
              <el-input v-model="taskForm.gzh_author" placeholder="请输入作者名称" size="large" maxlength="8"
                show-word-limit />
            </el-form-item>

            <el-form-item label="文章分类" prop="article_category_id">
              <el-select v-model="taskForm.article_category_id" placeholder="请选择文章分类" size="large" style="width: 100%"
                filterable>
                <el-option v-for="category in categoryList" :key="category.id" :label="category.article_category"
                  :value="category.id" />
              </el-select>
            </el-form-item>
          </el-col>

          <el-col :span="12">
            <el-form-item label="标题前缀">
              <el-input v-model="taskForm.add_prefix_to_title" placeholder="多个前缀用逗号分隔" size="large" />
            </el-form-item>

            <el-form-item label="标题后缀">
              <el-input v-model="taskForm.title_with_suffix" placeholder="多个后缀用逗号分隔" size="large" />
            </el-form-item>

            <el-form-item label="作品声明" prop=" content_source">
              <el-select v-model="taskForm.content_source" placeholder="请选择作品声明" size="large" style="width: 100%">
                <el-option label="不声明" :value="0" />
                <el-option label="取材网络" :value="1" />
                <el-option label="个人观点" :value="2" />
                <el-option label="引用AI" :value="3" />
                <el-option label="虚构演绎" :value="4" />
              </el-select>
            </el-form-item>

            <el-form-item label="是否原创">
              <el-switch v-model="taskForm.is_original" :active-value="1" :inactive-value="0" active-text="开启"
                inactive-text="关闭" />
            </el-form-item>

            <el-form-item label="投放广告">
              <el-switch v-model="taskForm.advertising_placement" :active-value="1" :inactive-value="0" active-text="开启"
                inactive-text="关闭" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-divider />

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="总发布数" prop="total_publications">
              <el-input-number v-model="taskForm.total_publications" :min="1" size="large" style="width: 100%" />
            </el-form-item>

            <el-form-item label="每日发布数" prop="daily_maximum_release">
              <el-input-number v-model="taskForm.daily_maximum_release" :min="1" size="large" style="width: 100%" />
            </el-form-item>

            <el-form-item label="执行间隔(秒)" prop="fb_Interval">
              <el-input-number v-model="taskForm.fb_Interval" :min="10" :max="86400" size="large" style="width: 100%" />
            </el-form-item>
          </el-col>

          <el-col :span="12">
            <el-form-item label="开始时间" prop="api_release_time_start">
              <el-time-select v-model="taskForm.api_release_time_start" :max-time="taskForm.api_release_time_end"
                placeholder="开始时间" start="00:00" step="01:00" end="24:00" size="large" style="width: 100%" />
            </el-form-item>

            <el-form-item label="结束时间" prop="api_release_time_end">
              <el-time-select v-model="taskForm.api_release_time_end" :min-time="taskForm.api_release_time_start"
                placeholder="结束时间" start="00:00" step="01:00" end="24:00" size="large" style="width: 100%" />
            </el-form-item>

            <el-form-item label="发布算法" prop="article_publishing_algorithm">
              <el-radio-group v-model="taskForm.article_publishing_algorithm" size="large">
                <el-radio :label="1">仅发未发过的文章</el-radio>
                <el-radio :label="2">重复发送</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="备注信息">
          <el-input v-model="taskForm.notes" placeholder="请输入备注信息" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitTaskForm">确认</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import {
  Search, Plus, Edit, Delete
} from '@element-plus/icons-vue'

export default {
  name: 'ToutiaoDraft',
  components: { Search, Plus, Edit, Delete },
  data() {
    return {
      loading: false,
      dialogVisible: false,
      dialogTitle: '新增任务',
      isEditMode: false,
      searchForm: {
        task_name: ''
      },
      taskForm: {
        id: null,
        task_name: '',
        gzh_id: null,
        gzh_author: '',
        article_category_id: null,
        original_reading_link: '',
        add_prefix_to_title: '',
        title_with_suffix: '',
        total_publications: 100,
        daily_maximum_release: 1,
        fb_Interval: 10,
        api_release_time_start: '00:00',
        api_release_time_end: '24:00',
        article_publishing_algorithm: 1,
        content_source: 0,
        is_original: 1, // 新增：是否原创，默认开启(1)
        advertising_placement: 1, // 新增：投放广告，默认开启(1)
        notes: ''
      },
      formRules: {
        task_name: [
          { required: true, message: '请输入任务名称', trigger: 'blur' }
        ],
        gzh_id: [
          {
            required: true,
            message: '请至少选择一个头条账号',
            trigger: 'change',
            type: 'array',
            validator: (rule, value, callback) => {
              if (this.isEditMode && (value === null || value === undefined || value === '')) {
                callback(new Error('请选择头条账号'));
              } else if (!this.isEditMode && (!Array.isArray(value) || value.length === 0)) {
                callback(new Error('请至少选择一个头条账号'));
              } else {
                callback();
              }
            }
          }
        ],
        article_category_id: [
          { required: true, message: '请选择文章分类', trigger: 'change' }
        ],
        total_publications: [
          { required: true, message: '请输入总发布数', trigger: 'blur' }
        ],
        daily_maximum_release: [
          { required: true, message: '请输入每日发布数', trigger: 'blur' }
        ],
        fb_Interval: [
          { required: true, message: '请输入执行间隔', trigger: 'blur' }
        ],
        api_release_time_start: [
          { required: true, message: '请选择开始时间', trigger: 'change' }
        ],
        api_release_time_end: [
          { required: true, message: '请选择结束时间', trigger: 'change' }
        ],
        article_publishing_algorithm: [
          { required: true, message: '请选择发布算法', trigger: 'change' }
        ]
      },
      taskList: [],
      gzhList: [],
      categoryList: [],
      headAdList: [],
      tailAdList: [],
      dialingList: [],
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      }
    }
  },
  computed: {
    userInfo() {
      return JSON.parse(Cookies.get('user_info') || '{}')
    }
  },
  mounted() {
    this.fetchTaskList();
    this.fetchGzhList();
    this.fetchCategoryList();
    this.fetchHeadAdList();
    this.fetchTailAdList();
    this.fetchDialingList();
  },
  methods: {
    // 获取头条名称
    getGzhName(id) {
      const gzh = this.gzhList.find(item => item.id === id);
      return gzh ? gzh.ghz_name : '未知';
    },

    // 获取分类名称
    getCategoryName(id) {
      const category = this.categoryList.find(item => item.id === id);
      return category ? category.article_category : '未知';
    },

    // 新增方法：格式化日期时间
    formatDateTime(dateString) {
      if (!dateString) return null;
      const date = new Date(dateString);
      return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')}`;
    },

    // 新增方法：获取状态文本
    getStatusText(status) {
      const statusMap = {
        1: '待启动',
        2: '发布中',
        3: '发布完成',
        4: '发布失败'
      };
      return statusMap[status] || '未知状态';
    },

    // 新增方法：获取状态标签类型
    getStatusTagType(status) {
      const typeMap = {
        1: 'info',    // 待启动 - 蓝色
        2: 'warning', // 发布中 - 橙色
        3: 'success', // 发布完成 - 绿色
        4: 'danger'   // 发布失败 - 红色
      };
      return typeMap[status] || '';
    },

    // 新增方法：切换任务状态
    async toggleTaskStatus(row) {
      try {
        // 添加加载状态
        row.statusLoading = true;


        const newQdStatus = row.qd_status === 1 ? 2 : 1;

        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/tt_article_task_management.php`,
          {
            action: 'update_status',
            store_id: this.userInfo.id,
            id: row.id,
            qd_status: newQdStatus
          }
        );

        if (data.success) {
          this.$message.success('状态更新成功');

          // 更新本地状态
          row.qd_status = newQdStatus;
          // 根据qd_status设置新状态
          row.status = newQdStatus === 1 ? 2 : 1;
        } else {
          this.$message.error(data.error || '状态更新失败');
        }
      } catch (error) {
        const errorMsg = error.response?.data?.error || error.message || '操作失败';
        this.$message.error(`操作失败: ${errorMsg}`);
        console.error('切换状态错误:', error);
      } finally {
        row.statusLoading = false;

      }
    },

    // 获取任务列表
    async fetchTaskList() {
      this.loading = true;
      try {
        const params = {
          action: this.searchForm.task_name ? 'search' : 'list',
          store_id: this.userInfo.id,
          page: this.pagination.page,
          page_size: this.pagination.pageSize
        };

        if (this.searchForm.task_name) {
          params.task_name = this.searchForm.task_name;
        }

        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/tt_article_task_management.php`,
          params
        );

        if (data.data) {
          this.taskList = data.data;
          this.pagination.total = data.pagination?.total || data.data.length;
          // 为每个任务添加状态加载标志
          this.taskList = data.data.map(task => ({
            ...task,
            statusLoading: false
          }));
        }
      } catch (error) {
        this.$message.error('获取任务列表失败');
        console.error('获取任务列表错误:', error);
      } finally {
        this.loading = false;
      }
    },

    // 获取头条列表
    async fetchGzhList() {
      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/gzh_auth_management.php`,
          {
            action: 'list_all',
            store_id: this.userInfo.id
          }
        );

        if (data.data) {
          // Filter to only include items where field_type === 4
          this.gzhList = data.data.filter(item => item.field_type === 4);
        }
      } catch (error) {
        console.error('获取头条列表错误:', error);
      }
    },

    // 获取分类列表
    async fetchCategoryList() {
      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/article_category_management.php`,
          {
            action: 'list_all',
            store_id: this.userInfo.id
          }
        );

        if (data.data) {
          this.categoryList = data.data;
        }
      } catch (error) {
        console.error('获取分类列表错误:', error);
      }
    },

    // 获取头部广告列表
    async fetchHeadAdList() {
      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/head_advertisement.php`,
          {
            action: 'list',
            store_id: this.userInfo.id
          }
        );

        if (data.data) {
          this.headAdList = data.data;
        }
      } catch (error) {
        console.error('获取头部广告列表错误:', error);
      }
    },

    // 获取尾部广告列表
    async fetchTailAdList() {
      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/tail_advertisement.php`,
          {
            action: 'list',
            store_id: this.userInfo.id
          }
        );

        if (data.data) {
          this.tailAdList = data.data;
        }
      } catch (error) {
        console.error('获取尾部广告列表错误:', error);
      }
    },

    // 获取拨号列表
    async fetchDialingList() {
      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/one_touch_dialing.php`,
          {
            action: 'list',
            store_id: this.userInfo.id
          }
        );

        if (data.data) {
          this.dialingList = data.data;
        }
      } catch (error) {
        console.error('获取拨号列表错误:', error);
      }
    },

    // 处理搜索
    handleSearch() {
      this.pagination.page = 1;
      this.fetchTaskList();
    },

    // 打开新增对话框
    openAddDialog() {
      this.dialogTitle = '新增任务';
      this.isEditMode = false;
      this.taskForm = {
        id: null,
        task_name: '',
        gzh_id: null,
        gzh_author: '',
        article_category_id: null,
        original_reading_link: '',
        add_prefix_to_title: '',
        title_with_suffix: '',
        total_publications: 100,
        daily_maximum_release: 1,
        fb_Interval: 10,
        api_release_time_start: '00:00',
        api_release_time_end: '24:00',
        article_publishing_algorithm: 1,
        content_source: 0,
        is_original: 1, // 默认开启合拍
        advertising_placement: 1, // 默认开启复制
        notes: ''
      };
      this.dialogVisible = true;
    },
    // 打开编辑对话框
    openEditDialog(row) {
      this.dialogTitle = '编辑任务';
      this.isEditMode = true;
      this.taskForm = {
        ...row,
        gzh_id: row.gzh_id ? [row.gzh_id] : [], // 关键修改：将单个ID转换为数组
        api_release_time_start: this.formatTime(row.api_release_time_start),
        api_release_time_end: this.formatTime(row.api_release_time_end),
        content_source: row.content_source || 0,
        is_original: row.is_original !== undefined ? row.is_original : 1, // 默认开启合拍
        advertising_placement: row.advertising_placement !== undefined ? row.advertising_placement : 1 // 默认开启复制
      };
      this.dialogVisible = true;
    },

    // 格式化时间 (HH:MM)
    formatTime(timeValue) {
      if (!timeValue) return '00:00'; // 改为默认 00:00
      if (typeof timeValue === 'number') {
        return `${timeValue.toString().padStart(2, '0')}:00`;
      }
      return timeValue;
    },

    // 提交表单（新增/编辑）
    submitTaskForm() {
      this.$refs.taskForm.validate(async (valid) => {
        if (valid) {
          try {
            const action = this.isEditMode ? 'update' : 'add';
            const payload = {
              ...this.taskForm,
              store_id: this.userInfo.id
            };
            // 编辑模式下只取第一个gzh_id（保持向后兼容）
            if (this.isEditMode && Array.isArray(payload.gzh_id)) {
              payload.gzh_id = payload.gzh_id[0] || null;
            }
            // 转换时间格式为数字
            payload.api_release_time_start = parseInt(payload.api_release_time_start.split(':')[0]);
            // 转换结束时间（特殊处理24点）
            if (payload.api_release_time_end === '00:00') {
              payload.api_release_time_end = 24;
            } else {
              payload.api_release_time_end =
                parseInt(payload.api_release_time_end.split(':')[0]);
            }

            const { data } = await axios.post(
              `${config.BASE_URL}/shop/api/tt_article_task_management.php`,
              {
                action,
                ...payload
              }
            );

            if (data.success) {
              this.$message.success(this.isEditMode ? '更新成功' : '添加成功');
              this.dialogVisible = false;
              this.fetchTaskList();
            } else {
              this.$message.error(data.error || '操作失败');
            }
          } catch (error) {
            const errorMsg = error.response?.data?.error || error.message || '操作失败';
            this.$message.error(`操作失败: ${errorMsg}`);
            console.error('表单提交错误:', error);
          }
        }
      });
    },

    // 删除任务
    async handleDelete(id) {
      this.$confirm('确定要删除该任务吗？', '警告', {
        confirmButtonText: '确认删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const { data } = await axios.post(
            `${config.BASE_URL}/shop/api/tt_article_task_management.php`,
            {
              action: 'delete',
              id: id,
              store_id: this.userInfo.id
            }
          );

          if (data.success) {
            this.$message.success('删除成功');
            this.fetchTaskList();
          } else {
            this.$message.error(data.error || '删除失败');
          }
        } catch (error) {
          const errorMsg = error.response?.data?.error || error.message || '删除失败';
          this.$message.error(`删除失败: ${errorMsg}`);
          console.error('删除错误:', error);
        }
      }).catch(() => { });
    }
  }
}
</script>

<style scoped lang="scss">
.page-container {
  padding: 20px;
}

.settings-form {
  max-width: 95%;
  margin: 0 auto;
  padding: 32px 40px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
}

.page-title {
  font-size: 24px;
  color: #1f2937;
  margin-bottom: 32px;
  font-weight: 600;
  text-align: center;
  letter-spacing: 0.5px;
}

.search-bar {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;
}

.pagination-container {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
}

/* 表格样式优化 */
.el-table {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  overflow: hidden;

  :deep(.el-table__header) th {
    background-color: #f5f7fa;
    color: #606266;
    font-weight: 600;
  }

  :deep(.el-table__body) tr:hover>td {
    background-color: #f0f7ff !important;
  }
}

/* 操作按钮样式 */
.el-button {
  display: inline-flex;
  align-items: center;

  .el-icon {
    margin-right: 5px;
  }
}

/* 对话框样式优化 */
:deep(.el-dialog) {
  border-radius: 12px;

  .el-dialog__header {
    padding: 20px 24px;
    border-bottom: 1px solid #ebeef5;
    margin-right: 0;

    .el-dialog__title {
      font-weight: 600;
      color: #1f2937;
    }
  }

  .el-dialog__body {
    padding: 24px;
  }

  .el-dialog__footer {
    padding: 16px 24px;
    border-top: 1px solid #ebeef5;
  }
}

/* 表单验证提示样式 */
:deep(.el-form-item__error) {
  font-size: 12px;
  padding-top: 4px;
}

.el-divider {
  margin: 20px 0;
}
</style>